(() => {
  if (typeof window === 'undefined') {
    return;
  }

  // ---------------------------------------------------------------------------
  // Guards – ensure the script only runs once in the browser environment.
  // ---------------------------------------------------------------------------
  if (window.__productSectionScriptLoaded) {
    return;
  }

  window.__productSectionScriptLoaded = true;

  const initProductSection = sectionEl => {
    if (!sectionEl || sectionEl.dataset.productSectionInitialized === 'true') {
      return;
    }

    sectionEl.dataset.productSectionInitialized = 'true';

    // -------------------------------------------------------------------------
    // Accordion toggles
    // -------------------------------------------------------------------------
    const root = sectionEl.querySelector('[data-acc-root]');
    if (root) {
      const triggers = root.querySelectorAll('button[data-acc-target]');
      triggers.forEach(btn => {
        btn.addEventListener('click', () => {
          const id = btn.getAttribute('data-acc-target');
          const panel = document.getElementById(id);
          const expanded = btn.getAttribute('aria-expanded') === 'true';

          triggers.forEach(other => {
            if (other !== btn) {
              other.setAttribute('aria-expanded', 'false');
              const otherPanel = document.getElementById(other.getAttribute('data-acc-target'));
              if (otherPanel) {
                otherPanel.style.maxHeight = '0px';
              }
            }
          });

          if (expanded) {
            btn.setAttribute('aria-expanded', 'false');
            if (panel) {
              panel.style.maxHeight = '0px';
            }
          } else {
            btn.setAttribute('aria-expanded', 'true');
            if (panel) {
              panel.style.maxHeight = panel.scrollHeight + 'px';
            }
          }
        });
      });
    }

    // -------------------------------------------------------------------------
    // Badge click → scroll to & open "Matière" accordion
    // -------------------------------------------------------------------------
    const badgeTrigger = sectionEl.querySelector('[data-scroll-to-matiere]');
    if (badgeTrigger) {
      const handleBadgeClick = () => {
        // The "Matière" accordion is the first .faq-question inside this section
        const matiereBtn = sectionEl.querySelector('.faq-question[data-faq-target]');
        if (!matiereBtn) return;

        const isExpanded = matiereBtn.getAttribute('aria-expanded') === 'true';
        if (!isExpanded) {
          matiereBtn.click();
        }

        // Scroll to the accordion item with a small offset for the sticky header
        requestAnimationFrame(() => {
          const item = matiereBtn.closest('.faq-item');
          const target = item || matiereBtn;
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      };

      badgeTrigger.addEventListener('click', handleBadgeClick);
      badgeTrigger.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleBadgeClick();
        }
      });
    }

    // -------------------------------------------------------------------------
    // Mobile media gallery with dots and arrows
    // -------------------------------------------------------------------------
    sectionEl.querySelectorAll('[data-mobile-gallery]').forEach(gallery => {
      const track = gallery.querySelector('[data-mobile-gallery-track]');
      const dotsWrapper = gallery.querySelector('[data-mobile-gallery-dots]');
      const prevBtn = gallery.querySelector('[data-mobile-gallery-prev]');
      const nextBtn = gallery.querySelector('[data-mobile-gallery-next]');
      if (!track || !dotsWrapper) {
        return;
      }

      const allSlides = Array.from(track.querySelectorAll('[data-mobile-gallery-slide]'));
      if (!allSlides.length) {
        return;
      }
      let slides = allSlides.filter(s => !s.classList.contains('media-filtered-out'));

      const labelTemplate = dotsWrapper.getAttribute('data-label-template') || 'Media %index%';
      const state = { activeIndex: 0, showArrows: false };

      const scrollToIndex = index => {
        const targetSlide = slides[index];
        if (!targetSlide) {
          return;
        }
        const targetLeft = targetSlide.offsetLeft;
        const scrollOptions = { left: targetLeft, behavior: 'smooth' };
        if (typeof track.scrollTo === 'function') {
          try {
            track.scrollTo(scrollOptions);
          } catch (err) {
            track.scrollLeft = targetLeft;
          }
        } else {
          track.scrollLeft = targetLeft;
        }
        renderDots(index);
      };

      const updateArrowState = () => {
        if (prevBtn) {
          const shouldDisable = !state.showArrows || state.activeIndex <= 0;
          prevBtn.disabled = shouldDisable;
          prevBtn.classList.toggle('pointer-events-none', shouldDisable);
          prevBtn.classList.toggle('opacity-40', state.showArrows && state.activeIndex <= 0);
        }
        if (nextBtn) {
          const shouldDisable = !state.showArrows || state.activeIndex >= slides.length - 1;
          nextBtn.disabled = shouldDisable;
          nextBtn.classList.toggle('pointer-events-none', shouldDisable);
          nextBtn.classList.toggle('opacity-40', state.showArrows && state.activeIndex >= slides.length - 1);
        }
      };

      const setArrowVisibility = show => {
        state.showArrows = show;
        [prevBtn, nextBtn].forEach(btn => {
          if (!btn) {
            return;
          }
          btn.classList.toggle('invisible', !show);
          btn.classList.toggle('opacity-0', !show);
          if (!show) {
            btn.classList.add('pointer-events-none');
          } else {
            btn.classList.remove('pointer-events-none');
          }
          btn.setAttribute('aria-hidden', show ? 'false' : 'true');
          btn.tabIndex = show ? 0 : -1;
        });
        updateArrowState();
      };

      const createDot = (index, size) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.dataset.index = String(index);
        btn.className = 'flex-shrink-0 rounded-full transition-all duration-300 ease-out bg-gray-400/80 opacity-80';

        if (size === 'lg') {
          btn.classList.add('h-2', 'w-2');
        } else if (size === 'md') {
          btn.classList.add('h-1.5', 'w-1.5');
        } else {
          btn.classList.add('h-1', 'w-1');
        }

        const labelIndex = Number.isFinite(index) ? index + 1 : state.activeIndex + 1;
        btn.setAttribute('aria-label', labelTemplate.replace('%index%', String(labelIndex)));

        if (index === state.activeIndex) {
          btn.classList.remove('bg-gray-400/80', 'opacity-80');
          btn.classList.add('bg-white', 'opacity-100', 'scale-110', 'shadow-sm', 'shadow-gray-500/40');
          btn.setAttribute('aria-current', 'true');
        }

        btn.addEventListener('click', () => scrollToIndex(index));
        dotsWrapper.appendChild(btn);
      };

      const renderDots = (activeIdx = state.activeIndex) => {
        state.activeIndex = Math.max(0, Math.min(slides.length - 1, activeIdx));
        dotsWrapper.innerHTML = '';

        const maxMain = Math.min(3, slides.length);
        const mainIndices = [];

        const addIndex = idx => {
          if (idx >= 0 && idx < slides.length && !mainIndices.includes(idx)) {
            mainIndices.push(idx);
          }
        };

        if (slides.length <= maxMain) {
          for (let i = 0; i < slides.length; i += 1) {
            mainIndices.push(i);
          }
        } else {
          addIndex(state.activeIndex);
          let offset = 1;
          while (mainIndices.length < maxMain) {
            addIndex(state.activeIndex - offset);
            if (mainIndices.length < maxMain) {
              addIndex(state.activeIndex + offset);
            }
            offset += 1;
          }
          mainIndices.sort((a, b) => a - b);
        }

        const firstMain = mainIndices[0];
        const lastMain = mainIndices[mainIndices.length - 1];

        if (firstMain > 0) {
          if (firstMain > 1) {
            createDot(0, 'sm');
          }
          createDot(firstMain - 1, 'md');
        }

        mainIndices.forEach(idx => {
          createDot(idx, 'lg');
        });

        if (lastMain < slides.length - 1) {
          createDot(lastMain + 1, 'md');
          if (lastMain < slides.length - 2) {
            createDot(slides.length - 1, 'sm');
          }
        }

        updateArrowState();
      };

      const updateActiveFromScroll = () => {
        const trackRect = track.getBoundingClientRect();
        const trackCenter = trackRect.left + trackRect.width / 2;
        let closestIndex = state.activeIndex;
        let minDistance = Number.POSITIVE_INFINITY;

        slides.forEach((slide, idx) => {
          const slideRect = slide.getBoundingClientRect();
          const slideCenter = slideRect.left + slideRect.width / 2;
          const distance = Math.abs(slideCenter - trackCenter);
          if (distance < minDistance) {
            minDistance = distance;
            closestIndex = idx;
          }
        });

        if (closestIndex !== state.activeIndex) {
          renderDots(closestIndex);
        }
      };

      let ticking = false;
      const onScroll = () => {
        if (ticking) {
          return;
        }
        ticking = true;
        window.requestAnimationFrame(() => {
          updateActiveFromScroll();
          ticking = false;
        });
      };

      track.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', () => updateActiveFromScroll());

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          const targetIndex = Math.max(0, state.activeIndex - 1);
          scrollToIndex(targetIndex);
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          const targetIndex = Math.min(slides.length - 1, state.activeIndex + 1);
          scrollToIndex(targetIndex);
        });
      }

      if (prevBtn || nextBtn) {
        const desktopMediaQuery = window.matchMedia('(pointer: fine) and (hover: hover)');
        const handleArrowVisibility = mq => {
          setArrowVisibility(Boolean(mq.matches));
        };
        handleArrowVisibility(desktopMediaQuery);
        if (typeof desktopMediaQuery.addEventListener === 'function') {
          desktopMediaQuery.addEventListener('change', handleArrowVisibility);
        } else if (typeof desktopMediaQuery.addListener === 'function') {
          desktopMediaQuery.addListener(handleArrowVisibility);
        }
      }

      renderDots(state.activeIndex);
      gallery.__scrollToIndex = scrollToIndex;
      gallery.__getSlides = () => slides;
      gallery.__setActiveIndex = index => renderDots(index);
      gallery.__rebuildAfterFilter = () => {
        slides = allSlides.filter(s => !s.classList.contains('media-filtered-out'));
        if (!slides.length) return;
        state.activeIndex = 0;
        renderDots(0);
        scrollToIndex(0);
        updateArrowState();
      };
    });

    // -------------------------------------------------------------------------
    // Product image lightbox
    // -------------------------------------------------------------------------
    (() => {
      const mediaDataEl = sectionEl.querySelector('[data-lightbox-media]');
      if (!mediaDataEl) {
        return;
      }

      let mediaItems = [];
      try {
        mediaItems = JSON.parse(mediaDataEl.textContent || '[]');
      } catch (err) {
        console.warn('Unable to parse lightbox media JSON', err);
        return;
      }

      if (!Array.isArray(mediaItems) || mediaItems.length === 0) {
        return;
      }

      // Filtered view: indices into mediaItems that are currently visible
      let visibleIndices = mediaItems.map((_, i) => i);

      const lightbox = sectionEl.querySelector('[data-product-lightbox]');
      if (!lightbox) {
        return;
      }

      const imageEl = lightbox.querySelector('[data-lightbox-image]');
      const loaderEl = lightbox.querySelector('[data-lightbox-loader]');
      const counterEl = lightbox.querySelector('[data-lightbox-counter]');
      const prevBtn = lightbox.querySelector('[data-lightbox-prev]');
      const nextBtn = lightbox.querySelector('[data-lightbox-next]');
      const closeBtn = lightbox.querySelector('[data-lightbox-close]');
      const thumbsContainer = lightbox.querySelector('[data-lightbox-thumbs]');
      const backdropEl = lightbox.querySelector('[data-lightbox-backdrop]');
      const clickableArea = lightbox.querySelector('[data-lightbox-clickable-area]');

      let currentIndex = 0;
      let isOpen = false;
      let lastFocusedEl = null;
      const preloadedSrcs = new Set();
      let loaderTimeout = null;

      // -- Preload helpers --
      const preloadImage = src => {
        if (!src || preloadedSrcs.has(src)) {
          return;
        }
        const img = new Image();
        img.src = src;
        preloadedSrcs.add(src);
      };

      const preloadAdjacent = visIdx => {
        [-1, 1].forEach(offset => {
          const adjVisIdx = visIdx + offset;
          if (adjVisIdx >= 0 && adjVisIdx < visibleIndices.length) {
            preloadImage(mediaItems[visibleIndices[adjVisIdx]].src);
          }
        });
      };

      // -- Thumbnails --
      const buildThumbnails = () => {
        if (!thumbsContainer) {
          return;
        }
        thumbsContainer.innerHTML = '';
        visibleIndices.forEach((realIdx, visIdx) => {
          const item = mediaItems[realIdx];
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className =
            'w-12 h-12 md:w-16 md:h-16 rounded-sm overflow-hidden border-2 transition-all duration-200 flex-shrink-0 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black';
          btn.setAttribute('aria-label', 'Voir image ' + (visIdx + 1));
          btn.dataset.thumbIndex = visIdx;

          const img = document.createElement('img');
          img.src = item.thumb;
          img.alt = '';
          img.className = 'w-full h-full object-cover';
          img.loading = 'lazy';
          img.draggable = false;
          btn.appendChild(img);

          btn.addEventListener('click', () => showImage(visIdx));
          thumbsContainer.appendChild(btn);
        });
      };

      const updateThumbnails = activeIndex => {
        if (!thumbsContainer) {
          return;
        }
        const thumbBtns = thumbsContainer.querySelectorAll('button');
        thumbBtns.forEach((btn, i) => {
          if (i === activeIndex) {
            btn.classList.remove('border-transparent', 'opacity-60');
            btn.classList.add('border-white', 'opacity-100');
            btn.setAttribute('aria-current', 'true');
          } else {
            btn.classList.remove('border-white', 'opacity-100');
            btn.classList.add('border-transparent', 'opacity-60');
            btn.removeAttribute('aria-current');
          }
        });
        const activeThumb = thumbBtns[activeIndex];
        if (activeThumb) {
          activeThumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
        }
      };

      // -- Display image at visible index --
      let currentVisIdx = 0;

      const showImage = visIdx => {
        currentVisIdx = Math.max(0, Math.min(visibleIndices.length - 1, visIdx));
        currentIndex = visibleIndices[currentVisIdx];
        const item = mediaItems[currentIndex];
        if (!item) {
          return;
        }

        // Clear any pending loader timeout
        if (loaderTimeout) {
          clearTimeout(loaderTimeout);
          loaderTimeout = null;
        }

        // Hide current image
        imageEl.style.opacity = '0';

        // Show loader only after 200ms delay (avoids flash for cached images)
        loaderTimeout = setTimeout(() => {
          if (loaderEl) {
            loaderEl.classList.remove('opacity-0', 'pointer-events-none');
            loaderEl.classList.add('opacity-100');
          }
        }, 200);

        const img = new Image();
        const hideLoader = () => {
          if (loaderTimeout) {
            clearTimeout(loaderTimeout);
            loaderTimeout = null;
          }
          if (loaderEl) {
            loaderEl.classList.remove('opacity-100');
            loaderEl.classList.add('opacity-0', 'pointer-events-none');
          }
        };

        img.onload = () => {
          hideLoader();
          imageEl.src = item.src;
          imageEl.alt = item.alt || '';
          imageEl.style.opacity = '1';
          preloadAdjacent(currentVisIdx);
        };
        img.onerror = () => {
          hideLoader();
          imageEl.src = item.src;
          imageEl.alt = item.alt || '';
          imageEl.style.opacity = '1';
        };
        img.src = item.src;

        // If cached, onload fires synchronously – handle already-loaded case
        if (img.complete && img.naturalWidth > 0) {
          hideLoader();
          imageEl.src = item.src;
          imageEl.alt = item.alt || '';
          imageEl.style.opacity = '1';
          preloadAdjacent(currentVisIdx);
        }

        // Update counter
        if (counterEl) {
          counterEl.textContent = (currentVisIdx + 1) + ' / ' + visibleIndices.length;
        }

        // Update arrows
        if (prevBtn) {
          prevBtn.disabled = currentVisIdx === 0;
          prevBtn.classList.toggle('opacity-30', currentVisIdx === 0);
        }
        if (nextBtn) {
          nextBtn.disabled = currentVisIdx === visibleIndices.length - 1;
          nextBtn.classList.toggle('opacity-30', currentVisIdx === visibleIndices.length - 1);
        }

        // Update thumbnails
        updateThumbnails(currentVisIdx);
      };

      // -- Open / Close --
      // `realIndex` is the index into the full mediaItems array (from data-lightbox-index)
      const open = (realIndex = 0) => {
        if (isOpen) {
          return;
        }
        isOpen = true;
        lastFocusedEl = document.activeElement;

        // Map real index to visible index
        let visIdx = visibleIndices.indexOf(realIndex);
        if (visIdx === -1) visIdx = 0;

        buildThumbnails();
        showImage(visIdx);

        document.body.style.overflow = 'hidden';
        lightbox.classList.remove('invisible', 'pointer-events-none');

        // Force reflow then trigger transition
        void lightbox.offsetHeight;
        lightbox.classList.remove('opacity-0');
        lightbox.classList.add('opacity-100');

        requestAnimationFrame(() => {
          lightbox.focus();
        });
      };

      const close = () => {
        if (!isOpen) {
          return;
        }
        isOpen = false;

        // Clear any pending loader timeout
        if (loaderTimeout) {
          clearTimeout(loaderTimeout);
          loaderTimeout = null;
        }

        lightbox.classList.remove('opacity-100');
        lightbox.classList.add('opacity-0');

        const cleanup = () => {
          lightbox.classList.add('invisible', 'pointer-events-none');
          document.body.style.overflow = '';
          if (loaderEl) {
            loaderEl.classList.remove('opacity-100');
            loaderEl.classList.add('opacity-0', 'pointer-events-none');
          }
          if (lastFocusedEl && typeof lastFocusedEl.focus === 'function') {
            lastFocusedEl.focus();
          }
        };

        const onEnd = () => {
          cleanup();
          lightbox.removeEventListener('transitionend', onEnd);
        };
        lightbox.addEventListener('transitionend', onEnd);

        // Safety timeout if transition doesn't fire
        setTimeout(() => {
          if (!isOpen) {
            cleanup();
          }
        }, 400);
      };

      const prev = () => {
        if (currentVisIdx > 0) {
          showImage(currentVisIdx - 1);
        }
      };

      const next = () => {
        if (currentVisIdx < visibleIndices.length - 1) {
          showImage(currentVisIdx + 1);
        }
      };

      // -- Event listeners: buttons --
      if (closeBtn) {
        closeBtn.addEventListener('click', close);
      }
      if (prevBtn) {
        prevBtn.addEventListener('click', prev);
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', next);
      }
      if (backdropEl) {
        backdropEl.addEventListener('click', close);
      }

      // -- Close on click outside image/buttons --
      if (clickableArea) {
        clickableArea.addEventListener('click', e => {
          // Only close if clicking directly on the clickable area (not bubbling from child elements)
          if (e.target === clickableArea) {
            close();
          }
        });
      }

      // -- Keyboard navigation & focus trap --
      lightbox.addEventListener('keydown', e => {
        if (!isOpen) {
          return;
        }
        switch (e.key) {
          case 'Escape':
            e.preventDefault();
            close();
            break;
          case 'ArrowLeft':
            e.preventDefault();
            prev();
            break;
          case 'ArrowRight':
            e.preventDefault();
            next();
            break;
          case 'Tab': {
            // Focus trap inside lightbox
            const focusable = Array.from(
              lightbox.querySelectorAll(
                'button:not([disabled]):not([tabindex="-1"]), [tabindex="0"]'
              )
            ).filter(el => {
              // Only include visible elements
              return el.offsetParent !== null || el.closest('[class*="hidden"]') === null;
            });
            if (!focusable.length) {
              break;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey) {
              if (document.activeElement === first || document.activeElement === lightbox) {
                e.preventDefault();
                last.focus();
              }
            } else {
              if (document.activeElement === last || document.activeElement === lightbox) {
                e.preventDefault();
                first.focus();
              }
            }
            break;
          }
        }
      });

      // -- Touch swipe support for mobile --
      let touchStartX = 0;
      let touchStartY = 0;
      let touchDeltaX = 0;
      let isHorizontalSwipe = null;

      lightbox.addEventListener(
        'touchstart',
        e => {
          if (!isOpen) {
            return;
          }
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
          touchDeltaX = 0;
          isHorizontalSwipe = null;
        },
        { passive: true }
      );

      lightbox.addEventListener(
        'touchmove',
        e => {
          if (!isOpen) {
            return;
          }
          const dx = e.touches[0].clientX - touchStartX;
          const dy = e.touches[0].clientY - touchStartY;

          // Determine swipe direction on first significant movement
          if (isHorizontalSwipe === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
            isHorizontalSwipe = Math.abs(dx) > Math.abs(dy);
          }

          if (isHorizontalSwipe) {
            e.preventDefault();
            touchDeltaX = dx;
          }
        },
        { passive: false }
      );

      lightbox.addEventListener(
        'touchend',
        () => {
          if (!isOpen) {
            return;
          }
          const threshold = 50;
          if (isHorizontalSwipe) {
            if (touchDeltaX > threshold) {
              prev();
            } else if (touchDeltaX < -threshold) {
              next();
            }
          }
          touchDeltaX = 0;
          isHorizontalSwipe = null;
        },
        { passive: true }
      );

      // -- Bind trigger elements (gallery images) --
      sectionEl.querySelectorAll('[data-lightbox-trigger]').forEach(trigger => {
        trigger.addEventListener('click', e => {
          // Don't open if user was scrolling the mobile gallery
          if (e.defaultPrevented) {
            return;
          }
          const index = parseInt(trigger.dataset.lightboxIndex || '0', 10);
          open(index);
        });

        trigger.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const index = parseInt(trigger.dataset.lightboxIndex || '0', 10);
            open(index);
          }
        });
      });

      // -- Expose filter API for variant-based gallery filtering --
      sectionEl.__lightboxSetFilter = filterFn => {
        if (typeof filterFn === 'function') {
          visibleIndices = mediaItems
            .map((item, i) => (filterFn(item, i) ? i : -1))
            .filter(i => i !== -1);
        } else {
          visibleIndices = mediaItems.map((_, i) => i);
        }
        // If lightbox is currently open, rebuild thumbnails and re-show
        if (isOpen) {
          const realIdx = currentIndex;
          let newVisIdx = visibleIndices.indexOf(realIdx);
          if (newVisIdx === -1) newVisIdx = 0;
          buildThumbnails();
          showImage(newVisIdx);
        }
      };
    })();

    // -------------------------------------------------------------------------
    // Product form, variant selection and dynamic UI updates
    // -------------------------------------------------------------------------
    const formEl = sectionEl.querySelector('form');
    if (formEl) {
      formEl.addEventListener('submit', async event => {
        event.preventDefault();

        const submitBtn = formEl.querySelector('[type="submit"]');
        if (submitBtn) {
          submitBtn.classList.add('opacity-50', 'cursor-not-allowed');
        }

        try {
          const formData = new FormData(formEl);
          const config = {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          };

          const routes = window.Shopify && window.Shopify.routes;
          const root = routes ? routes.root : '/';
          const response = await fetch(`${root}cart/add.js`, config);

          if (response.ok) {
            if (typeof window.refreshCartDrawer === 'function') {
              await window.refreshCartDrawer({ openAfter: true });
            } else {
              window.location.href = `${root}cart`;
            }
          } else {
            const data = await response.json();
            console.error('Add to cart error:', data);

            const description = data && data.description ? String(data.description) : '';
            const message = description || 'Impossible d’ajouter cet article. Merci de vérifier la disponibilité puis de réessayer.';

            if (typeof window.showCartDrawerError === 'function') {
              window.showCartDrawerError(message);
            }
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
          const friendly = 'Un problème de connexion est survenu. Merci de réessayer.';

          if (typeof window.showCartDrawerError === 'function') {
            window.showCartDrawerError(friendly);
          }
        } finally {
          if (submitBtn) {
            submitBtn.classList.remove('opacity-50', 'cursor-not-allowed');
          }
        }
      });

      const qtyInput = formEl.querySelector('input[name="quantity"]');
      if (qtyInput) {
        formEl.querySelectorAll('[data-qty]').forEach(ctrl => {
          ctrl.addEventListener('click', () => {
            const dir = ctrl.getAttribute('data-qty');
            const val = parseInt(qtyInput.value || '1', 10);
            qtyInput.value = Math.max(1, val + (dir === 'inc' ? 1 : -1));
          });
        });
      }

      const variantDataEl = sectionEl.querySelector('[data-product-variants]');
      if (!variantDataEl) {
        return;
      }

      let variants = [];
      try {
        variants = JSON.parse(variantDataEl.textContent || '[]');
      } catch (err) {
        console.error('Unable to parse product variants JSON', err);
        variants = [];
      }

      if (!Array.isArray(variants) || variants.length === 0) {
        return;
      }

      // -----------------------------------------------------------------------
      // Initial data and DOM handles
      // -----------------------------------------------------------------------
      const defaultsEl = sectionEl.querySelector('[data-product-defaults]');
      let defaults = {};
      if (defaultsEl) {
        try {
          defaults = JSON.parse(defaultsEl.textContent || '{}');
        } catch (err) {
          console.warn('Unable to parse product defaults JSON', err);
        }
      }

      const moneyFormatEl = sectionEl.querySelector('[data-product-money-format]');
      let moneyFormat = null;
      if (moneyFormatEl) {
        try {
          moneyFormat = JSON.parse(moneyFormatEl.textContent || 'null');
        } catch (err) {
          console.warn('Unable to parse money format JSON', err);
        }
      }

      const variantIdInput = formEl.querySelector('[data-variant-id-input]');
      const priceEl = sectionEl.querySelector('[data-product-price]');
      const comparePriceEl = sectionEl.querySelector('[data-product-compare-price]');
      const availabilityEl = sectionEl.querySelector('[data-product-availability]');
      const addToCartBtn = formEl.querySelector('[data-product-add-to-cart]');
      const addToCartLabel = formEl.querySelector('[data-product-add-to-cart-label]');
      const optionInputs = Array.from(formEl.querySelectorAll('[data-option-input]'));
      const optionGroups = Array.from(formEl.querySelectorAll('[data-variant-option]'));
      const optionLabels = Array.from(formEl.querySelectorAll('[data-option-label]'));
      const mainMediaItems = Array.from(sectionEl.querySelectorAll('[data-product-media-item]'));
      const mobileGalleryEl = sectionEl.querySelector('[data-mobile-gallery]');
      const badgeTarget = sectionEl.querySelector('[data-variant-html="badge"]');
      const materialTarget = sectionEl.querySelector('[data-variant-html="material"]');
      const dimensionsTarget = sectionEl.querySelector('[data-variant-html="dimensions"]');
      const origineTarget = sectionEl.querySelector('[data-variant-html="origine"]');
      const livraisonTarget = sectionEl.querySelector('[data-variant-html="livraison"]');
      const conseilsTarget = sectionEl.querySelector('[data-variant-text="conseils"]');

      const addToCartText = addToCartBtn ? addToCartBtn.dataset.addToCartText || '' : '';
      const soldOutText = addToCartBtn ? addToCartBtn.dataset.soldOutText || '' : '';
      const unavailableText = addToCartBtn ? addToCartBtn.dataset.unavailableText || '' : '';
      const currency = defaults.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';

      // -----------------------------------------------------------------------
      // Helper utilities
      // -----------------------------------------------------------------------
      const formatMoney = value => {
        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function' && moneyFormat) {
          return Shopify.formatMoney(value, moneyFormat);
        }
        const amount = Number(value || 0) / 100;
        try {
          return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
        } catch (err) {
          return amount.toFixed(2);
        }
      };

      const setAvailabilityMessage = (message, isHidden) => {
        if (!availabilityEl) {
          return;
        }
        if (isHidden) {
          availabilityEl.classList.add('hidden');
          availabilityEl.textContent = '';
        } else {
          availabilityEl.classList.remove('hidden');
          availabilityEl.textContent = message || '';
        }
      };

      const setAddToCartState = (isAvailable, message) => {
        if (!addToCartBtn || !addToCartLabel) {
          return;
        }
        addToCartBtn.disabled = !isAvailable;
        addToCartLabel.textContent = message || (isAvailable ? addToCartText : soldOutText);
      };

      const updatePriceDisplay = variant => {
        if (!variant) {
          return;
        }
        if (priceEl) {
          priceEl.innerHTML = formatMoney(variant.price);
        }
        if (!comparePriceEl) {
          return;
        }
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.classList.remove('hidden');
          comparePriceEl.textContent = formatMoney(variant.compare_at_price);
        } else {
          comparePriceEl.classList.add('hidden');
          comparePriceEl.textContent = '';
        }
      };

      const setVariantMetafields = variant => {
        if (!variant) {
          return;
        }

        const fallbackBadgeHtml = defaults.badge_html || '';
        const fallbackMaterial = defaults.material || '';
        const fallbackMaterialHtml = defaults.material_html || '';
        const fallbackDimensionsHtml = defaults.dimensions_html || '';
        const fallbackOrigine = defaults.origine || '';
        const fallbackLivraison = defaults.livraison || '';
        const fallbackConseils = defaults.conseils || '';

        const setContent = (target, value, fallback, useHtml = false) => {
          if (!target) {
            return;
          }
          const resolved = (value && value.length ? value : fallback) || '';
          if (useHtml) {
            target.innerHTML = resolved;
          } else {
            target.textContent = resolved;
          }
        };

        if (badgeTarget) {
          badgeTarget.innerHTML = (variant.badge_html != null ? variant.badge_html : fallbackBadgeHtml) || '';
        }
        const materialHtml = (variant.material_html && variant.material_html.length ? variant.material_html : '') || '';
        setContent(materialTarget, materialHtml, fallbackMaterialHtml, true);
        if (materialTarget) {
          materialTarget.dataset.materialName = (variant.material && variant.material.length ? variant.material : fallbackMaterial) || '';
        }
        const dimensionsHtml = (variant.dimensions_html && variant.dimensions_html.length ? variant.dimensions_html : '') || '';
        setContent(dimensionsTarget, dimensionsHtml, fallbackDimensionsHtml, true);
        setContent(origineTarget, variant.origine, fallbackOrigine, true);
        setContent(livraisonTarget, variant.livraison, fallbackLivraison, true);
        setContent(conseilsTarget, variant.conseils, fallbackConseils, true);
      };

      const getSelectedOptions = () => {
        const selections = new Array(optionGroups.length);
        optionInputs.forEach(input => {
          if (input.checked) {
            const index = Number.parseInt(input.dataset.optionIndex || '0', 10);
            selections[index] = input.value;
          }
        });
        return selections;
      };

      const findVariantByOptions = options =>
        variants.find(variant => {
          if (!variant || !Array.isArray(variant.options)) {
            return false;
          }
          return variant.options.every((value, index) => options[index] === value);
        });

      const setActiveMedia = mediaId => {
        const idToActivate =
          mediaId || sectionEl.dataset.initialMediaId || (mainMediaItems[0] ? mainMediaItems[0].dataset.mediaId : null);
        if (!idToActivate) {
          return;
        }
        mainMediaItems.forEach(item => {
          const matches = item.dataset.mediaId === String(idToActivate);
          if (matches) {
            item.setAttribute('data-active-media', 'true');
          } else {
            item.removeAttribute('data-active-media');
          }
        });
        // Desktop: no viewport scroll — image visibility is handled via
        // data-active-media attribute and CSS/gallery filtering.
        if (
          mobileGalleryEl &&
          typeof mobileGalleryEl.__scrollToIndex === 'function' &&
          typeof mobileGalleryEl.__getSlides === 'function'
        ) {
          const slides = mobileGalleryEl.__getSlides();
          const targetIndex = slides.findIndex(slide => slide.dataset.mediaId === String(idToActivate));
          if (targetIndex >= 0) {
            mobileGalleryEl.__scrollToIndex(targetIndex);
          }
        }
      };

      const updateOptionAvailability = activeVariant => {
        optionGroups.forEach(group => {
          const optionIndex = Number.parseInt(group.dataset.optionIndex || '0', 10);
          const inputs = group.querySelectorAll('[data-option-input]');
          inputs.forEach(input => {
            const selectedOptions = getSelectedOptions();
            selectedOptions[optionIndex] = input.value;
            const matchingVariant = findVariantByOptions(selectedOptions);
            const label = input.closest('[data-option-label]');
            if (!matchingVariant) {
              if (!input.checked) {
                input.disabled = true;
              }
              if (label) {
                label.classList.add('opacity-40', 'pointer-events-none');
                label.dataset.variantAvailable = 'false';
                const hint = label.querySelector('[data-option-soldout-hint]');
                if (hint) {
                  hint.classList.add('hidden');
                }
              }
            } else {
              input.disabled = false;
              if (label) {
                label.classList.remove('opacity-40', 'pointer-events-none');
                label.dataset.variantAvailable = matchingVariant.available ? 'true' : 'false';
                const hint = label.querySelector('[data-option-soldout-hint]');
                if (hint) {
                  hint.classList.toggle('hidden', matchingVariant.available);
                }
              }
            }
          });
        });
        if (!activeVariant) {
          return;
        }
        optionLabels.forEach(label => {
          const optionIndex = Number.parseInt(label.dataset.optionIndex || '0', 10);
          const optionValue = label.dataset.optionValue;
          const isSelected = activeVariant.options && activeVariant.options[optionIndex] === optionValue;
          label.dataset.selected = isSelected ? 'true' : 'false';
        });
      };

      const syncOptionInputs = variant => {
        if (!variant || !Array.isArray(variant.options)) {
          return;
        }
        variant.options.forEach((value, index) => {
          optionInputs.forEach(input => {
            if (Number.parseInt(input.dataset.optionIndex || '0', 10) === index) {
              input.checked = input.value === value;
            }
          });
        });
      };

      const updateUrl = variant => {
        if (!variant) {
          return;
        }
        if (typeof window === 'undefined' || !window.history || typeof window.history.replaceState !== 'function') {
          return;
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      };

      const setAvailabilityAndAddToCartState = (isAvailable, message) => {
        if (isAvailable) {
          setAvailabilityMessage('', true);
          setAddToCartState(true, message);
        } else {
          setAvailabilityMessage(message, false);
          setAddToCartState(false, message);
        }
      };

      // -----------------------------------------------------------------------
      // Variant-based gallery filtering
      // Detect which option drives image filtering by checking which option's
      // values appear as image alt texts (works for couleur, lot, or any type).
      // -----------------------------------------------------------------------
      let galleryFilterOptionIndex = null;
      const allFilterValues = new Set();

      // Collect all media alt texts for quick lookup
      const mediaAlts = new Set();
      mainMediaItems.forEach(item => {
        const alt = (item.dataset.mediaAlt || '').trim().toLowerCase();
        if (alt) mediaAlts.add(alt);
      });
      sectionEl.querySelectorAll('[data-mobile-gallery-slide]').forEach(slide => {
        const alt = (slide.dataset.mediaAlt || '').trim().toLowerCase();
        if (alt) mediaAlts.add(alt);
      });

      // Find the first option whose values match any media alt text
      optionGroups.forEach(group => {
        if (galleryFilterOptionIndex !== null) return; // already found
        const optionIndex = Number.parseInt(group.dataset.optionIndex || '0', 10);
        const optionValues = new Set();

        variants.forEach(v => {
          if (v.options && v.options[optionIndex]) {
            optionValues.add(v.options[optionIndex].toLowerCase());
          }
        });

        // Check if any media alt matches this option's values
        let hasMatch = false;
        optionValues.forEach(val => {
          if (mediaAlts.has(val)) hasMatch = true;
        });

        if (hasMatch) {
          galleryFilterOptionIndex = optionIndex;
          optionValues.forEach(val => allFilterValues.add(val));
        }
      });

      const isGenericAlt = alt => {
        if (!alt || !alt.trim()) return true;
        return !allFilterValues.has(alt.toLowerCase());
      };

      const mobileSlides = sectionEl.querySelectorAll('[data-mobile-gallery-slide]');

      let _filterTimer = null;
      let _filterInitialised = false;
      const desktopGalleryEl = sectionEl.querySelector('[data-product-desktop-gallery]');

      const applyDesktopFilter = (target) => {
        mainMediaItems.forEach(item => {
          const alt = item.dataset.mediaAlt || '';
          const shouldShow = isGenericAlt(alt) || alt.toLowerCase() === target;
          item.classList.toggle('media-filtered-out', !shouldShow);
        });
      };

      const filterGalleryByOption = couleurValue => {
        if (galleryFilterOptionIndex === null) return;
        const target = (couleurValue || '').toLowerCase();

        if (!_filterInitialised) {
          // First call (page load) — apply instantly without animation
          _filterInitialised = true;
          applyDesktopFilter(target);
        } else if (desktopGalleryEl) {
          // Subsequent calls — fade container out, swap items, fade back in.
          // Cancel any pending swap from a previous rapid click.
          if (_filterTimer) {
            clearTimeout(_filterTimer);
            _filterTimer = null;
          }

          // Phase 1: fade the whole gallery container to opacity 0
          desktopGalleryEl.classList.add('gallery-fading');

          // Phase 2: after the CSS transition ends, swap items and fade back in
          _filterTimer = setTimeout(() => {
            applyDesktopFilter(target);
            desktopGalleryEl.classList.remove('gallery-fading');
            _filterTimer = null;
          }, 150); // matches the CSS transition duration
        } else {
          // No desktop gallery (edge case) — instant swap
          applyDesktopFilter(target);
        }

        // Mobile gallery
        mobileSlides.forEach(slide => {
          const alt = slide.dataset.mediaAlt || '';
          const show = isGenericAlt(alt) || alt.toLowerCase() === target;
          slide.classList.toggle('media-filtered-out', !show);
        });

        // Rebuild mobile gallery dots & scroll
        if (mobileGalleryEl && typeof mobileGalleryEl.__rebuildAfterFilter === 'function') {
          mobileGalleryEl.__rebuildAfterFilter();
        }

        // Update lightbox filter
        if (typeof sectionEl.__lightboxSetFilter === 'function') {
          sectionEl.__lightboxSetFilter(item => {
            const alt = item.alt || '';
            return isGenericAlt(alt) || alt.toLowerCase() === target;
          });
        }
      };

      const handleVariantChange = variant => {
        if (!variant) {
          setAvailabilityAndAddToCartState(false, unavailableText || soldOutText);
          return;
        }

        if (variantIdInput) {
          variantIdInput.value = variant.id;
        }

        updatePriceDisplay(variant);
        setVariantMetafields(variant);
        if (galleryFilterOptionIndex !== null && variant.options) {
          filterGalleryByOption(variant.options[galleryFilterOptionIndex]);
        }
        setActiveMedia(variant.featured_media_id);
        updateOptionAvailability(variant);
        updateUrl(variant);

        if (variant.available) {
          setAvailabilityAndAddToCartState(true, addToCartText);
        } else {
          setAvailabilityAndAddToCartState(false, soldOutText);
        }
      };

      optionInputs.forEach(input => {
        input.addEventListener('change', () => {
          const selections = getSelectedOptions();
          const matchingVariant = findVariantByOptions(selections);
          if (matchingVariant) {
            handleVariantChange(matchingVariant);
          } else {
            setAvailabilityAndAddToCartState(false, unavailableText || soldOutText);
          }
        });
      });

      const initialVariantId = Number.parseInt(
        sectionEl.dataset.initialVariantId || (variantIdInput ? variantIdInput.value : ''),
        10
      );
      let currentVariant = variants.find(variant => variant.id === initialVariantId);
      if (!currentVariant) {
        currentVariant = findVariantByOptions(getSelectedOptions()) || variants[0];
      }

      if (currentVariant) {
        syncOptionInputs(currentVariant);
        handleVariantChange(currentVariant);
      }
    }

    // -------------------------------------------------------------------------
    // Bidirectional sticky columns (desktop only)
    // -------------------------------------------------------------------------
    (() => {
      const HEADER_OFFSET = 96; // 6rem – header height (5rem) + 1rem gap
      const BOTTOM_MARGIN = 24;
      const lgMq = window.matchMedia('(min-width: 1024px)');

      const leftCol = sectionEl.querySelector('[data-sticky-col="left"]');
      const rightCol = sectionEl.querySelector('[data-sticky-col="right"]');
      if (!leftCol || !rightCol) return;

      const leftInner = leftCol.querySelector('[data-sticky-inner]');
      const rightInner = rightCol.querySelector('[data-sticky-inner]');
      if (!leftInner || !rightInner) return;

      const update = () => {
        if (!lgMq.matches) {
          leftInner.style.removeProperty('top');
          rightInner.style.removeProperty('top');
          return;
        }

        const vh = window.innerHeight;
        const available = vh - HEADER_OFFSET;

        const computeTop = el => {
          const h = el.getBoundingClientRect().height;
          if (h <= available) return HEADER_OFFSET;
          return -(h - vh + BOTTOM_MARGIN);
        };

        leftInner.style.top = computeTop(leftInner) + 'px';
        rightInner.style.top = computeTop(rightInner) + 'px';
      };

      const ro = new ResizeObserver(() => requestAnimationFrame(update));
      ro.observe(leftInner);
      ro.observe(rightInner);

      window.addEventListener('resize', update, { passive: true });

      if (typeof lgMq.addEventListener === 'function') {
        lgMq.addEventListener('change', update);
      } else if (typeof lgMq.addListener === 'function') {
        lgMq.addListener(update);
      }

      update();
    })();
  };

  // ---------------------------------------------------------------------------
  // Section hydration – initialise all instances within a given DOM root.
  // ---------------------------------------------------------------------------
  const hydrateProductSections = root => {
    const scope = root || document;
    scope.querySelectorAll('[data-product-section]').forEach(sectionEl => {
      initProductSection(sectionEl);
    });
  };

  // ---------------------------------------------------------------------------
  // Initial load & Theme Editor section reload support.
  // ---------------------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => hydrateProductSections());
  } else {
    hydrateProductSections();
  }

  document.addEventListener('shopify:section:load', event => {
    hydrateProductSections(event.target);
  });
})();

